import { z } from 'zod'

import { http } from '@/shared/api'

import type { AdminPlan, CreatePlanInput, UpdatePlanInput } from '../model/types'

const priceSchema = z.object({
  duration_days: z.number(),
  amount_cents: z.number(),
  is_active: z.boolean(),
})

const planSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name_ru: z.string(),
  name_en: z.string(),
  tier: z.number(),
  max_campaigns: z.number().nullable(),
  is_popular: z.boolean(),
  is_active: z.boolean(),
  prices: z.array(priceSchema).nullable(),
})

const plansSchema = z.object({ plans: z.array(planSchema).nullable() })

export async function fetchAdminPlans(signal?: AbortSignal): Promise<AdminPlan[]> {
  const { plans } = await http.get('admin/plans', { schema: plansSchema, signal })
  return (plans ?? [])
    .map((raw) => ({
      id: raw.id,
      slug: raw.slug,
      nameRu: raw.name_ru,
      nameEn: raw.name_en,
      tier: raw.tier,
      maxCampaigns: raw.max_campaigns,
      isPopular: raw.is_popular,
      isActive: raw.is_active,
      prices: (raw.prices ?? []).map((price) => ({
        durationDays: price.duration_days,
        amountCents: price.amount_cents,
        isActive: price.is_active,
      })),
    }))
    .sort((a, b) => a.tier - b.tier)
}

const createdSchema = z.object({ plan_id: z.string() })

export async function createPlan(input: CreatePlanInput): Promise<string> {
  const { plan_id } = await http.post('admin/plans', {
    json: {
      slug: input.slug,
      name_ru: input.nameRu,
      name_en: input.nameEn,
      tier: input.tier,
      max_campaigns: input.maxCampaigns,
      is_popular: input.isPopular,
      prices: input.prices.map((price) => ({
        duration_days: price.durationDays,
        amount_cents: price.amountCents,
      })),
    },
    schema: createdSchema,
  })
  return plan_id
}

export async function updatePlan(id: string, input: UpdatePlanInput): Promise<void> {
  await http.patch(`admin/plans/${id}`, {
    json: {
      ...(input.nameRu !== undefined && { name_ru: input.nameRu }),
      ...(input.nameEn !== undefined && { name_en: input.nameEn }),
      ...(input.isPopular !== undefined && { is_popular: input.isPopular }),
      ...(input.isActive !== undefined && { is_active: input.isActive }),
      ...(input.maxCampaigns !== undefined && { max_campaigns: input.maxCampaigns }),
      ...(input.prices !== undefined && {
        prices: input.prices.map((price) => ({
          duration_days: price.durationDays,
          amount_cents: price.amountCents,
        })),
      }),
    },
  })
}

export async function deletePlan(id: string): Promise<void> {
  await http.delete(`admin/plans/${id}`)
}
